import { useState, type ReactNode } from 'react';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { Tooltip } from 'radix-ui';
import { setApiNavigator, setOnUnauthenticated } from '../lib/api';
import { createQueryClient, qk } from '../lib/queryClient';
import { Toaster } from '../components/ui';
import { CookieBanner } from '../components/shared/CookieBanner';
import { createAppRoutes } from './router';

/** Providers shared by the app and tests. */
export function AppProviders({ client, children }: { client: QueryClient; children: ReactNode }) {
  return (
    <QueryClientProvider client={client}>
      <Tooltip.Provider delayDuration={300}>
        {children}
        <Toaster />
      </Tooltip.Provider>
    </QueryClientProvider>
  );
}

function createApp() {
  const queryClient = createQueryClient();
  const router = createBrowserRouter(createAppRoutes());
  // Let the api client redirect without a full page reload (401 → /sign-in, mfa_required → /sign-in/totp).
  setApiNavigator((to, opts) => void router.navigate(to, { replace: opts?.replace }));
  setOnUnauthenticated(() => queryClient.setQueryData(qk.me, null));
  return { queryClient, router };
}

export function App() {
  const [{ queryClient, router }] = useState(createApp);
  return (
    <AppProviders client={queryClient}>
      <RouterProvider router={router} />
      <CookieBanner />
    </AppProviders>
  );
}
